import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { deleteAdminGroup, setAdminGroupRights } from '../api';
import AdminDeleteControl from './AdminDeleteControl';
import SearchField, { isSearchReady } from './SearchField';
import PagedPicker from './PagedPicker';
import Button from './ui/Button';

/**
 * What each group grants: rights down the side, groups across the top.
 *
 * Rights are the fixed axis — there are fourteen of them and the list only
 * changes when the code does — while groups are added as the platform grows, so
 * they are the axis that scrolls. Two ways to choose which groups stand there:
 * a search, which answers "where is this group", and the full list a page at a
 * time, which answers "what is there at all".
 */
function AdminGroupMatrix({ groups, rights, onChanged }) {
  const { t } = useTranslation();

  const initial = useMemo(() => {
    const map = new Map();

    for (const group of groups) {
      map.set(group.group_id, new Set(group.rights ?? []));
    }

    return map;
  }, [groups]);

  const [selected, setSelected] = useState(initial);
  const [savingGroupId, setSavingGroupId] = useState(null);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [query, setQuery] = useState('');
  const [isBrowsing, setBrowsing] = useState(false);
  const [pickedIds, setPickedIds] = useState([]);

  const isSearching = isSearchReady(query);

  // Browsing wins over searching: the picked groups are an explicit choice,
  // and a leftover search string should not quietly narrow them.
  const visibleGroups = useMemo(() => {
    if (isBrowsing) {
      return groups.filter((group) => pickedIds.includes(group.group_id));
    }

    if (!isSearching) {
      return [];
    }

    const needle = query.trim().toLowerCase();

    return groups.filter((group) =>
      [group.group_name, group.department_name]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(needle),
    );
  }, [groups, query, isSearching, isBrowsing, pickedIds]);

  function togglePicked(groupId) {
    setPickedIds((current) =>
      current.includes(groupId)
        ? current.filter((id) => id !== groupId)
        : [...current, groupId],
    );
  }

  useEffect(() => {
    setSelected(initial);
  }, [initial]);

  function toggle(groupId, rightName) {
    setError('');
    setSuccessMessage('');

    setSelected((current) => {
      const next = new Map(current);
      const held = new Set(next.get(groupId) ?? []);

      if (held.has(rightName)) {
        held.delete(rightName);
      } else {
        held.add(rightName);
      }

      next.set(groupId, held);
      return next;
    });
  }

  function isDirty(group) {
    const now = selected.get(group.group_id) ?? new Set();
    const was = initial.get(group.group_id) ?? new Set();

    return (
      now.size !== was.size ||
      [...now].some((right) => !was.has(right))
    );
  }

  async function handleSave(group) {
    try {
      setSavingGroupId(group.group_id);
      setError('');
      setSuccessMessage('');

      const names = selected.get(group.group_id) ?? new Set();

      const rightIds = rights
        .filter((right) => names.has(right.right_name))
        .map((right) => right.right_id);

      await setAdminGroupRights(group.group_id, rightIds);

      setSuccessMessage(t('adminPanel.groupRightsSaved', { name: group.group_name }));

      if (onChanged) {
        await onChanged();
      }
    } catch (submitError) {
      console.error('Failed to save group rights:', submitError);
      setError(submitError?.message || t('adminPanel.errorGeneric'));
    } finally {
      setSavingGroupId(null);
    }
  }

  if (groups.length === 0) {
    return (
      <p className="admin-user-list-empty">{t('adminPanel.noGroups')}</p>
    );
  }

  return (
    <div className="admin-memberships">
      {error && (
        <div className="login-message login-message-error" role="alert">
          <i className="fa-solid fa-circle-exclamation" />
          <span>{error}</span>
        </div>
      )}

      {successMessage && (
        <div className="login-message login-message-success" role="status">
          <i className="fa-solid fa-circle-check" />
          <span>{successMessage}</span>
        </div>
      )}

      <div className="admin-user-search-row">
        <SearchField
          value={query}
          onChange={setQuery}
          placeholder={t('adminPanel.groupSearchPlaceholder')}
          ariaLabel={t('adminPanel.groupSearchAriaLabel')}
        />

        <Button
          variant="secondary"
          aria-pressed={isBrowsing}
          onClick={() => setBrowsing((browsing) => !browsing)}
        >
          <i className="fa-solid fa-list" />
          {isBrowsing
            ? t('adminPanel.hideAllUsers')
            : t('adminPanel.showAllUsers')}
        </Button>
      </div>

      {isBrowsing && (
        <PagedPicker
          items={groups}
          getKey={(group) => group.group_id}
          selectedKeys={pickedIds}
          onToggle={togglePicked}
          renderPrimary={(group) => group.group_name}
          renderSecondary={(group) =>
            group.is_platform
              ? t('adminPanel.platformScope')
              : group.department_name
          }
        />
      )}

      {visibleGroups.length === 0 ? (
        <p className="admin-user-list-empty">
          {isBrowsing
            ? t('adminPanel.pickGroupsToEdit')
            : isSearching
              ? t('adminPanel.noGroupsFound')
              : t('adminPanel.searchToStartGroups')}
        </p>
      ) : (
        <div
          className={`admin-memberships-scroll${
            isBrowsing ? ' admin-user-selection' : ''
          }`}
        >
          <table className="admin-memberships-table">
            <thead>
              <tr>
                <th scope="col">{t('adminPanel.rightColumn')}</th>

                {visibleGroups.map((group) => (
                  <th scope="col" key={group.group_id}>
                    <span className="admin-memberships-scope">
                      {group.group_name}
                    </span>

                    <span className="admin-memberships-hint">
                      {group.is_platform
                        ? t('adminPanel.platformScope')
                        : group.department_name}
                      {' · '}
                      {t('adminPanel.groupMembers', { count: group.members })}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {rights.map((right) => (
                <tr key={right.right_id}>
                  <th scope="row" title={right.description || ''}>
                    <span className="admin-memberships-scope">
                      {right.right_name}
                    </span>
                  </th>

                  {visibleGroups.map((group) => (
                    <td key={group.group_id}>
                      <label className="admin-memberships-cell">
                        <input
                          type="checkbox"
                          checked={
                            selected.get(group.group_id)?.has(right.right_name) ??
                            false
                          }
                          disabled={savingGroupId !== null}
                          title={`${group.group_name} · ${right.right_name}`}
                          onChange={() => toggle(group.group_id, right.right_name)}
                        />

                        <span className="visually-hidden">
                          {`${group.group_name} · ${right.right_name}`}
                        </span>
                      </label>
                    </td>
                  ))}
                </tr>
              ))}

              <tr>
                <th scope="row" />

                {visibleGroups.map((group) => (
                  <td key={group.group_id}>
                    <Button
                      variant="primary"
                      className="admin-group-save"
                      disabled={savingGroupId !== null || !isDirty(group)}
                      onClick={() => handleSave(group)}
                    >
                      {savingGroupId === group.group_id
                        ? t('adminPanel.saving')
                        : t('adminPanel.save')}
                    </Button>
                  </td>
                ))}
              </tr>

              <tr>
                <th scope="row" />

                {visibleGroups.map((group) => (
                  <td key={group.group_id}>
                    <AdminDeleteControl
                      label={t('adminPanel.deleteGroup')}
                      disabledReason={
                        group.acl_articles + group.acl_news > 0
                          ? t('adminPanel.groupInUse', {
                              articles: group.acl_articles,
                              news: group.acl_news,
                            })
                          : group.keeps_administration
                            ? t('adminPanel.groupKeepsAdministration')
                            : null
                      }
                      confirmText={t('adminPanel.deleteGroupConfirm', {
                        name: group.group_name,
                        count: group.members,
                      })}
                      onDelete={async () => {
                        await deleteAdminGroup(group.group_id);
                        setPickedIds((current) =>
                          current.filter((id) => id !== group.group_id),
                        );
                        setSuccessMessage(
                          t('adminPanel.groupDeleted', { name: group.group_name }),
                        );

                        if (onChanged) {
                          await onChanged();
                        }
                      }}
                    />
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default AdminGroupMatrix;
